import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';
import { generateContractPool } from '../entities/ContractFactory';

/**
 * Find staff who are idle — not on active project, not training/leave,
 * not locked to a contract, and not already claimed this tick.
 */
function findIdleStaff(state: SimulationState, alreadyClaimed: Set<string>): string[] {
  const lockedToContracts = new Set(
    state.contracts.flatMap((c) => c.lockedStaffIds),
  );
  return state.staff
    .filter((s) => {
      if (s.isTraining || s.isOnLeave) return false;
      if (lockedToContracts.has(s.id)) return false;
      if (alreadyClaimed.has(s.id)) return false;
      if (s.teamId && state.teams.some((t) => t.id === s.teamId && t.assignedProjectId)) return false;
      return true;
    })
    .map((s) => s.id);
}

export function contractSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  // Track staff claimed by idle-mode contracts this tick
  // so one person can't work on multiple contracts simultaneously
  const claimedThisTick = new Set<string>();

  for (const contract of state.contracts) {
    if (!contract.isActive) continue;

    // Determine who's working this tick
    if (contract.assignmentMode === 'specific') {
      // Locked staff only — filter out departed
      contract.lockedStaffIds = contract.lockedStaffIds.filter(
        (id) => state.staff.some((s) => s.id === id),
      );
      contract.assignedStaffIds = contract.lockedStaffIds.filter((id) => {
        const s = state.staff.find((st) => st.id === id);
        return s && !s.isTraining && !s.isOnLeave;
      });
      // Mark locked staff as claimed
      for (const id of contract.assignedStaffIds) claimedThisTick.add(id);
    } else {
      // Auto-assign idle staff, respecting already-claimed
      const idle = findIdleStaff(state, claimedThisTick);
      contract.assignedStaffIds = idle.slice(0, contract.requiredStaff);
      // Mark these as claimed so other contracts can't use them
      for (const id of contract.assignedStaffIds) claimedThisTick.add(id);
    }

    // Calculate progress
    let tickProgress = 0;
    for (const staffId of contract.assignedStaffIds) {
      const staff = state.staff.find((s) => s.id === staffId);
      if (!staff) continue;
      const relevantStat = staff.stats[contract.requiredStat] ?? 10;
      tickProgress += (relevantStat / 30) * 1;
    }

    contract.progress += (tickProgress / contract.durationTicks) * 100;

    // Deadline countdown
    if (contract.deadlineTicks > 0) {
      contract.deadlineTicks--;

      if (contract.deadlineTicks <= 0 && contract.progress < 100) {
        state.company.funds -= contract.failurePenalty;
        state.company.totalExpenses += contract.failurePenalty;
        state.company.reputation = Math.max(0, state.company.reputation - 5);

        events.push({
          type: 'CONTRACT_FAILED',
          contractId: contract.id,
          clientName: contract.clientName,
        });
        events.push({
          type: 'FUNDS_CHANGED',
          newFunds: state.company.funds,
          delta: -contract.failurePenalty,
          reason: `Contract failed: ${contract.clientName}`,
        });

        contract.isActive = false;
        continue;
      }
    }

    // Completion
    if (contract.progress >= 100) {
      state.company.funds += contract.reward;
      state.company.totalRevenue += contract.reward;
      state.company.reputation = Math.min(100, state.company.reputation + contract.reputationReward);

      events.push({
        type: 'CONTRACT_COMPLETED',
        contractId: contract.id,
        clientName: contract.clientName,
        reward: contract.reward,
      });
      events.push({
        type: 'FUNDS_CHANGED',
        newFunds: state.company.funds,
        delta: contract.reward,
        reason: `Contract: ${contract.clientName}`,
      });

      contract.isActive = false;
    }
  }

  // Remove completed/failed
  state.contracts = state.contracts.filter((c) => c.isActive);

  // Expire from pool
  for (const contract of state.contractPool) {
    contract.deadline--;
  }
  state.contractPool = state.contractPool.filter((c) => c.deadline > 0);

  // Refresh pool
  const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
  const refreshInterval = 4 * ticksPerWeek;
  if (state.company.currentTick > 0 && state.company.currentTick % refreshInterval === 0) {
    const newContracts = generateContractPool(rng, themeData, state.company.level);
    state.contractPool.push(...newContracts);
  }

  return events;
}
